import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { descendantsOf, type Container } from "@egalregal/shared";
import { requests } from "../../shared/api";
import { log } from "../../logger";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import { DestinationEntryList, destinationEntryChoices } from "./DestinationEntryList";
import { DestinationParentPicker } from "./DestinationParentPicker";

interface MoveEntryDialogProps {
  open: boolean;
  container: Container;
  onClose: () => void;
  /**
   * Owner of the entry that currently holds `container` — undefined when it
   * lives in the caller's own home, the shared entry's owner otherwise. The
   * ownership-confirm step appears exactly when the destination entry's owner
   * differs (a transfer crosses home boundaries, not just shared entries).
   */
  sourceOwner?: string;
  /** Called with the destination entry name after a successful move. */
  onMoved?: (entryName: string, droppedPlacements: number) => void;
}

type Step = "entry" | "parent" | "confirm";

/**
 * Unified move flow (container-catalog-transfer 02 + 03 + 06): step 1 picks
 * the destination entry among own entries plus writable shared entries —
 * shared rendered `name@owner`, read-only hidden. When the destination home
 * differs from the source (sourceOwner versus the chosen entry's owner) the
 * flow goes entry → owner-confirm (ownership transfer, root placement only);
 * otherwise entry → parent (root or any container of the entry). One confirm
 * commits entry reassignment and re-parenting together; the parent choice
 * excludes the moved container and all its descendants (cycle safety).
 *
 * The entry list and parent picker are the same primitives the bulk move uses
 * (the shared destination entry list and parent picker), so the two flows
 * cannot drift.
 */
export function MoveEntryDialog({ open, container, sourceOwner, onClose, onMoved }: MoveEntryDialogProps) {
  const t = useT();
  const entries = useAppStore((s) => s.entries);
  const sharedWithMe = useAppStore((s) => s.sharedWithMe);
  const containers = useAppStore((s) => s.containers);
  const move = useAppStore((s) => s.move);
  const [step, setStep] = useState<Step>("entry");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [destContainers, setDestContainers] = useState<Container[]>([]);
  const [parentId, setParentId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const entryChoices = destinationEntryChoices(entries, sharedWithMe);
  const selected = entryChoices.find((c) => c.id === selectedId);
  /** A move crosses home boundaries (ownership transfer) when the destination
   * entry's owner differs from the entry that currently holds the container. */
  const crossesHome = selected !== undefined && selected.owner !== sourceOwner;
  const entryName = selected?.label ?? selectedId ?? "";

  const reset = () => {
    setStep("entry");
    setSelectedId(null);
    setDestContainers([]);
    setParentId(null);
    setBusy(false);
  };

  const advance = async () => {
    if (!selectedId) return;
    setBusy(true);
    try {
      if (crossesHome) {
        setStep("confirm");
      } else {
        setDestContainers(await requests.containers.list(selectedId));
        setStep("parent");
      }
    } catch (err) {
      log.warn({ err }, "[move] destination entry load failed");
    } finally {
      setBusy(false);
    }
  };

  const commit = async (chosenParentId: string | null) => {
    if (!selectedId) return;
    setBusy(true);
    try {
      // A transfer relocates the subtree as a root of the destination entry
      // and must carry the explicit ownership confirmation; a same-home move
      // keeps its chosen parent placement and needs no confirmation.
      const result = crossesHome
        ? await move(container.id, null, container.version, selectedId, true)
        : await move(container.id, chosenParentId, container.version, selectedId);
      onMoved?.(entryName, result?.droppedPlacements ?? 0);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  // The moved container's own entry currently holds it (and its subtree), so
  // self + descendants are known from the store — never offer one as a parent.
  const excluded = new Set([
    container.id,
    ...descendantsOf(containers, container.id).map((c) => c.id),
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      data-testid="move-entry-dialog"
      TransitionProps={{ onExited: reset }}
    >
      <DialogTitle>{t("moveTitle")}</DialogTitle>
      {step === "entry" ? (
        <>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("movePickEntry")}
            </Typography>
            <DestinationEntryList
              choices={entryChoices}
              selectedId={selectedId}
              onSelect={setSelectedId}
              sourceOwner={sourceOwner}
              testIdPrefix="move-entry"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>{t("cancel")}</Button>
            <Button
              variant="contained"
              disabled={!selectedId || busy}
              data-testid="move-entry-confirm"
              onClick={() => void advance()}
            >
              {t("next")}
            </Button>
          </DialogActions>
        </>
      ) : step === "parent" ? (
        <>
          <DialogContent data-testid="move-parent-step">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("movePickParent", { entry: entryName })}
            </Typography>
            <DestinationParentPicker
              containers={destContainers}
              entryLabel={entryName}
              value={parentId}
              onChange={setParentId}
              excludeIds={excluded}
              testIdPrefix="move-parent"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStep("entry")} disabled={busy} data-testid="move-parent-back">
              {t("back")}
            </Button>
            <Button onClick={() => void commit(null)} disabled={busy} data-testid="move-to-root">
              {t("moveToRoot")}
            </Button>
            <Button
              variant="contained"
              disabled={parentId === null || busy}
              data-testid="move-parent-confirm"
              onClick={() => void commit(parentId)}
            >
              {t("move")}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent data-testid="move-transfer-step">
            <Typography variant="h6" sx={{ mb: 1 }} data-testid="move-transfer-title">
              {t("moveTransferTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="move-transfer-body">
              {t("moveTransferBody", { entry: entryName })}
            </Typography>
            <Typography variant="body2" color="warning.main" data-testid="move-transfer-warning">
              {t("moveTransferWarning")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStep("entry")} disabled={busy} data-testid="move-transfer-back">
              {t("back")}
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={busy}
              data-testid="move-transfer-confirm"
              onClick={() => void commit(null)}
            >
              {t("moveTransferConfirm")}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
